import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { FaPlus } from 'react-icons/fa';
import { format, startOfDay } from 'date-fns';
import AppointmentCard from './AppointmentCard';
import DayNavigator from './DayNavigator';
import DoctorChips from './DoctorChips';
import OutOfHoursRow from './OutOfHoursRow';
import {
  countItems, countLabel, dayKey, dayTitle, groupByDay, isSameDay, itemKind, monthDoctors, monthTitle,
  nowLineIndex, releasedStatusIds, shortNames, weekOf,
} from './agendaDay';
import { joinNames, outOfHoursRows, withOutOfHours } from './outOfHours';
import { scrollPageTo, useMinuteTick } from './listShared';
import './AgendaDayList.css';

// Lista do dia (Agenda até 992 px; aba Diário de Horários). Só props: os dados são o mês que a página já carrega, e abrir,
// criar, filtrar e atualizar ficam com a página. state: 'loading' | 'ready' | 'error' (do mês do dia escolhido, com os filtros).
// outOfHours: as faixas "Fora do horário" do mês, as mesmas do calendário (outOfHoursEvents)
const AgendaDayList = ({
  day, events, outOfHours = [], statusList, state, refreshError, filtered, scrollRequest, onScrollDone, returnFocus, chip = 'all', onChipChange,
  onSelectDay, onToday, onOpen, onNew, onFilters, onRetry, onRefresh,
}) => {
  const ready = state === 'ready';
  const now = new Date();
  const today = startOfDay(now);
  const isToday = isSameDay(day, today);
  useMinuteTick(isToday && ready);

  const [announcement, setAnnouncement] = useState('');
  const rootRef = useRef(null);
  const barRef = useRef(null);
  const headRef = useRef(null);
  const listRef = useRef(null);
  const fabRef = useRef(null);

  const releasedIds = useMemo(() => releasedStatusIds(statusList), [statusList]);
  const byDay = useMemo(() => groupByDay(events), [events]);
  const doctors = useMemo(() => monthDoctors(events), [events]);
  const names = useMemo(() => shortNames(doctors), [doctors]);

  // Chips só com mais de um doutor no mês (o login de doutor só vê os dele). A escolha (chip) fica na URL, com a página:
  // recarregar mantém o doutor. Doutor da URL sem agendamento no mês carregado: mostra todos
  const showChips = doctors.length > 1;
  const activeChip = showChips && doctors.some((doctor) => doctor.id === chip) ? chip : 'all';
  const chipDoctor = doctors.find((doctor) => doctor.id === activeChip) ?? null;
  const ofChip = (items) => (activeChip === 'all' ? items : items.filter((item) => item.tecnico?.id === activeChip));

  const allOfDay = ready ? (byDay.get(dayKey(day)) ?? []) : [];
  const items = ofChip(allOfDay);
  const counts = countItems(items, releasedIds);
  // Faixas "Fora do horário" do dia: as do doutor do chip; em Todos, as dos doutores do mês (as colunas do calendário).
  // Entram na ordem do dia, entre os cartões, e não contam como agendamento nem como bloqueio
  const bands = ready ? outOfHoursRows(outOfHours, day, activeChip === 'all' ? doctors.map((doctor) => doctor.id) : [activeChip]) : [];
  const timeline = withOutOfHours(items, bands);
  const nowIndex = isToday ? nowLineIndex(timeline, now) : -1;
  const title = dayTitle(day, today);
  const lowerTitle = `${title.charAt(0).toLowerCase()}${title.slice(1)}`;

  // Faixa da semana: o pedido do mês traz as semanas inteiras das pontas (loadRange), então os 7 dias têm número
  const weekCounts = new Map();
  if (ready) {
    for (const date of weekOf(day)) weekCounts.set(dayKey(date), countItems(ofChip(byDay.get(dayKey(date)) ?? []), releasedIds).appointments);
  }
  const chipCounts = new Map([
    ['all', countItems(allOfDay, releasedIds).appointments],
    ...doctors.map((doctor) => [doctor.id, countItems(allOfDay.filter((item) => item.tecnico?.id === doctor.id), releasedIds).appointments]),
  ]);

  // Anuncia a troca de dia, o carregamento e os erros (a atualização de 30 s não é anunciada). A falha de uma atualização
  // tem anúncio próprio, só quando acontece: com a faixa na tela, a troca de dia continua sendo anunciada
  const headerHeight = useRef(46); // --agenda-header-h, lido do CSS ao montar
  const summary = `${title}: ${countLabel(counts)}`;
  useEffect(() => {
    if (state === 'error') setAnnouncement(`Não foi possível carregar a agenda de ${monthTitle(day, today)}.`);
    else if (state === 'loading') setAnnouncement('Carregando agendamentos…');
    else setAnnouncement(summary);
  }, [dayKey(day), state, activeChip]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (refreshError) setAnnouncement('Não foi possível atualizar.');
  }, [refreshError]);

  useLayoutEffect(() => {
    headerHeight.current = parseFloat(getComputedStyle(rootRef.current).getPropertyValue('--agenda-header-h')) || 46;
  }, []);

  // Tab e Shift+Tab: o item focado que ficou atrás do cabeçalho e da barra fixa rola para baixo dela (o navegador acha que
  // ele está à vista e não rola). Os controles da própria barra ficam de fora: estão sempre à vista. Um scroll-padding na
  // página faria o navegador subir até o topo ao focar a barra
  useEffect(() => {
    const root = rootRef.current;
    let frame = 0;
    const onFocusIn = (event) => {
      if (barRef.current?.contains(event.target)) return;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const bar = barRef.current;
        const rect = event.target.getBoundingClientRect();
        if (!bar || !rect.height) return;
        const limit = headerHeight.current + (getComputedStyle(bar).position === 'sticky' ? bar.offsetHeight : 0);
        if (rect.top < limit) scrollPageTo(rect.top + window.scrollY - limit - 8);
      });
    };
    root.addEventListener('focusin', onFocusIn);
    return () => {
      cancelAnimationFrame(frame);
      root.removeEventListener('focusin', onFocusIn);
    };
  }, []);

  // Detalhes (ou novo) fechado: o foco volta ao cartão (ou ao +) se ficou perdido na página. Tenta depois da animação do
  // modal e de novo quando os dados mudam (a recarga pode mover o cartão), por alguns segundos
  useEffect(() => {
    if (!returnFocus) return undefined;
    const tryFocus = () => {
      if (Date.now() > returnFocus.until) return;
      if (document.activeElement && document.activeElement !== document.body) return;
      const target = returnFocus.id ? listRef.current?.querySelector(`li[data-id="${returnFocus.id}"] > button`) : fabRef.current;
      target?.focus({ preventScroll: true });
    };
    const timers = [350, 1000].map((delay) => setTimeout(tryFocus, delay));
    return () => timers.forEach(clearTimeout);
  }, [returnFocus, events]);

  // Rolagem pedida pela página: até "agora" (com um cartão de contexto acima) ou de volta ao título do dia.
  // Espera os cartões do dia; a atualização de 30 s não pede rolagem
  useLayoutEffect(() => {
    if (!scrollRequest || !ready) return;
    const bar = barRef.current;
    const offset = headerHeight.current + (bar && getComputedStyle(bar).position === 'sticky' ? bar.offsetHeight : 0) + 8;
    const pageTop = (element) => element.getBoundingClientRect().top + window.scrollY - offset;

    const context = scrollRequest.kind === 'now' ? listRef.current?.querySelector('[data-now]')?.previousElementSibling : null;
    if (context) {
      if (!scrollRequest.onlyIfTop || window.scrollY === 0) scrollPageTo(pageTop(context));
    } else if (headRef.current) {
      // Só sobe até o título; se ele já está à vista, a página fica onde está
      const top = pageTop(headRef.current);
      if (top < window.scrollY) scrollPageTo(top);
    }
    onScrollDone(scrollRequest);
  }, [scrollRequest, ready]); // eslint-disable-line react-hooks/exhaustive-deps

  const nowLine = <li key='agora' className='agenda-day-now' data-now=''>agora {format(now, 'HH:mm')}</li>;
  const renderEntry = (entry) => (entry.outOfHours
    ? <OutOfHoursRow key={entry.key} row={entry} doctorNames={joinNames(entry.doctorIds.map((id) => names.get(id)))} />
    : (
      <AppointmentCard
        key={entry.id}
        item={entry}
        kind={itemKind(entry, releasedIds)}
        doctorName={names.get(entry.tecnico?.id) || entry.tecnico?.nome}
        onOpen={onOpen}
      />
    ));

  let body;
  if (state === 'error') {
    body = (
      // Sem role="alert": a região aria-live já anuncia o erro (com os dois, o leitor de tela falava duas vezes)
      <div className='agenda-day-empty'>
        <p>Não foi possível carregar a agenda de {monthTitle(day, today)}.</p>
        <Button type='button' onClick={onRetry}>Tentar de novo</Button>
      </div>
    );
  } else if (!ready) {
    body = (
      <>
        <div className='agenda-day-skeleton' aria-hidden='true'><i /><i /><i /></div>
        <p className='agenda-day-loading'>Carregando agendamentos…</p>
      </>
    );
  } else if (!items.length) {
    if (chipDoctor && allOfDay.length) {
      body = (
        <div className='agenda-day-empty'>
          <p>Nenhum agendamento de {names.get(chipDoctor.id)} neste dia.</p>
          <Button type='button' variant='outline-primary' onClick={() => onChipChange('all')}>Ver todos</Button>
        </div>
      );
    } else if (filtered) {
      body = (
        <div className='agenda-day-empty'>
          <p>Nenhum agendamento neste dia com os filtros aplicados.</p>
          <Button type='button' variant='dark' onClick={onFilters}>Filtros</Button>
        </div>
      );
    } else {
      body = (
        <div className='agenda-day-empty'>
          <p>Nenhum agendamento neste dia.</p>
          <Button type='button' onClick={() => onNew(chipDoctor)}>+ Novo agendamento neste dia</Button>
        </div>
      );
    }
  } else {
    const rows = [];
    timeline.forEach((entry, index) => {
      if (index === nowIndex) rows.push(nowLine);
      rows.push(renderEntry(entry));
    });
    if (nowIndex === timeline.length) rows.push(nowLine);
    body = <ol className='agenda-day-list' aria-label={`Agendamentos de ${lowerTitle}`} ref={listRef}>{rows}</ol>;
  }

  // Dia sem agendamento: as faixas ficam abaixo do aviso (ex.: o doutor não atende neste dia), sem a linha "agora", que
  // levaria a rolagem para longe do aviso e do botão de novo agendamento
  if (ready && !items.length && bands.length) {
    body = <>{body}<ol className='agenda-day-list' aria-label={`Fora do horário em ${lowerTitle}`}>{bands.map(renderEntry)}</ol></>;
  }

  return (
    <section className='agenda-day' ref={rootRef} aria-label='Lista do dia'>
      <DayNavigator day={day} today={today} counts={weekCounts} onSelect={(date) => onSelectDay(date)} onToday={onToday} barRef={barRef} />

      {refreshError && ready ? (
        <div className='agenda-day-banner'>
          <span>Não foi possível atualizar.</span>
          <Button type='button' variant='outline-dark' size='sm' onClick={onRefresh}>Tentar de novo</Button>
        </div>
      ) : ''}

      <div className='agenda-day-head' ref={headRef}>
        <h2>{title}</h2>
        {ready ? <p className='agenda-day-count'>{countLabel(counts)}</p> : ''}
      </div>

      {ready && showChips ? <DoctorChips doctors={doctors} names={names} active={activeChip} counts={chipCounts} onChange={onChipChange} /> : ''}

      {body}

      <button type='button' className='agenda-day-fab' ref={fabRef} aria-label='Novo agendamento' title='Novo agendamento' onClick={() => onNew(chipDoctor)}>
        <FaPlus aria-hidden='true' />
      </button>

      <p className='visually-hidden' aria-live='polite'>{announcement}</p>
    </section>
  );
};

export default AgendaDayList;
